import { useRef } from 'react'
import placeholderImage from 'assets/images/placeholder.png'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Navigation, FreeMode } from 'swiper/modules'
import Link from 'components/Link'
import { useOnScreen } from 'data/hooks/useOnScreen'
import Image from 'components/Image'
import { GetInspiredDestinationRegionsQuery } from 'gql/graphql'
import classNames from 'classnames'
import { getPropertyTypeFromSlug } from '@ama-selections/ui'
import Paragraphs from 'components/home/common/typography/Paragraphs'
import Headers from 'components/home/common/typography/Headers'

interface CarouselGetInspiredProps {
  featuredRegions: GetInspiredDestinationRegionsQuery['regions']['data']
}

const CarouselGetInspired = ({
  featuredRegions,
}: CarouselGetInspiredProps) => {
  const componentRef = useRef<HTMLDivElement>(null)
  const navigationPrevRef = useRef(null)
  const navigationNextRef = useRef(null)
  const buttonClasses = classNames(
    'visible !w-40 !h-40 sm:!w-50 sm:!h-50 top-1/2 -mt-20 sm:-mt-25 !hidden',
    { 'lg:!hidden': featuredRegions.length < 5 },
  )
  const { initialLoad } = useOnScreen(componentRef)

  return (
    <div ref={componentRef}>
      {initialLoad && (
        <>
          <div className="relative flex items-center w-full min-w-0 gap-x-10 xl:gap-x-40 max-w-[1480px] lg:px-10 mx-auto">
            <div ref={navigationPrevRef} className={classNames('swiper-button-prev left-5 xl:left-40', buttonClasses)} />
            <div ref={navigationNextRef} className={classNames('swiper-button-next right-5 xl:right-40', buttonClasses)} />

            <Swiper
              className="max-w-[1280px] md:px-20"
              modules={[Navigation, FreeMode]}
              slidesPerView={'auto'}
              navigation={{
                prevEl: navigationPrevRef.current,
                nextEl: navigationNextRef.current,
              }}
              breakpoints={{
                640: {
                  spaceBetween: 33,
                  slidesOffsetBefore: 0,
                  slidesOffsetAfter: 0,
                },
              }}
              slidesOffsetBefore={20}
              slidesOffsetAfter={20}
              spaceBetween={20}
              freeMode={{
                enabled: true,
                sticky: true,
                momentum: true,
                momentumVelocityRatio: 0.25,
              }}
              onSwiper={(swiper: SwiperClass) => {
                setTimeout(() => {
                  if (swiper.params.navigation && typeof(swiper.params.navigation) !== 'boolean') {
                    // Timeout until prevEl & nextEl refs are defined
                    swiper.params.navigation.prevEl = navigationPrevRef.current
                    swiper.params.navigation.nextEl = navigationNextRef.current
                  }

                  // Re-initialise navigation
                  swiper.navigation.destroy()
                  swiper.navigation.init()
                  swiper.navigation.update()
                })
              }}
              observer={true}
            >
              {
                (featuredRegions ?? []).map(region => (
                  <SwiperSlide className="max-w-[221px] w-full group" key={region?.id}>
                    <Link
                      href={{
                        pathname: '/[country]/[region]',
                        query: {
                          country: region.country.slug,
                          region: region.slug,
                        },
                      }}
                      key={region?.id}>
                      <div className="relative h-full">
                        <Image
                          className="object-cover rounded-10 group-hover:brightness-90"
                          src={region?.main_media?.url ?? placeholderImage}
                          width={221}
                          height={315}
                        />
                      </div>

                      <Paragraphs.XS className="my-10 text-primary-fresh">
                        {region?.country?.value}
                      </Paragraphs.XS>

                      <Headers.BaseHeader className="mb-4 text-grey-900 !font-normal text-26">
                        {region?.value}
                      </Headers.BaseHeader>

                      <Paragraphs.XS className="text-grey-650">
                        {region?.property_count} {getPropertyTypeFromSlug(region.slug)} for Rent
                      </Paragraphs.XS>
                    </Link>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>

        </>
      )
      }
    </div>
  )
}

export default CarouselGetInspired
